// src/components/common/Footer/Footer.tsx
import React from 'react';
import styled from 'styled-components';
import { MapPin, Phone, Mail, Facebook, Instagram, Twitter, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const FooterContainer = styled.footer`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 4rem 2rem 2rem;
  margin-top: 5rem;
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterSection = styled.div`
  h3 {
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 1.5rem;
    font-size: 1.25rem;
    font-family: ${props => props.theme.fonts.primary};
    font-weight: 700;
  }
  
  p, li {
    opacity: 0.9;
    line-height: 1.8;
    font-size: 0.95rem;
  }
  
  ul {
    list-style: none;
  }
`;

const FooterLink = styled(Link)`
  display: block;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: all 0.3s ease;
  font-size: 0.95rem;

  &:hover {
    color: ${props => props.theme.colors.secondary};
    transform: translateX(5px);
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: start;
  gap: 12px;
  margin-bottom: 1rem;
  opacity: 0.9;

  svg {
    color: ${props => props.theme.colors.secondary};
    flex-shrink: 0;
    margin-top: 2px;
  }

  a {
    color: rgba(255, 255, 255, 0.9);
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: ${props => props.theme.colors.secondary};
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SocialLink = styled.a`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: ${props => props.theme.colors.secondary};
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(212, 165, 116, 0.4);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FooterBottom = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  opacity: 0.8;
  font-size: 0.9rem;
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const LegalLink = styled(Link)`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.secondary};
  }
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        {/* About Section */}
        <FooterSection>
          <h3>Nysdill Hotel Heathrow</h3>
          <p>
            Most reasonable short-stay accommodation provider near Heathrow Airport.
            Perfect for travelers catching flights or attending events. Your comfort is our priority.
          </p>
          <SocialLinks>
            <SocialLink
              href="https://facebook.com/nysdillaccommodation"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <Facebook />
            </SocialLink>
            <SocialLink
              href="https://instagram.com/nysdillaccommodation"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <Instagram />
            </SocialLink>
            <SocialLink
              href="https://twitter.com/nysdillhotel"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Twitter"
            >
              <Twitter />
            </SocialLink>
          </SocialLinks>
        </FooterSection>

        {/* Quick Links */}
        <FooterSection>
          <h3>Quick Links</h3>
          <ul>
            <li><FooterLink to="/">Home</FooterLink></li>
            <li><FooterLink to="/about">About Us</FooterLink></li>
            <li><FooterLink to="/rooms">Our Rooms</FooterLink></li>
            <li><FooterLink to="/amenities">Amenities</FooterLink></li>
            <li><FooterLink to="/attractions">Local Attractions</FooterLink></li>
            <li><FooterLink to="/contact">Contact Us</FooterLink></li>
          </ul>
        </FooterSection>

        {/* Contact Information */}
        <FooterSection>
          <h3>Contact Us</h3>
          <ContactItem>
            <MapPin size={18} />
            <div>
              21 Woodfield Road<br />
              Hounslow<br />
              TW4 6LL, United Kingdom
            </div>
          </ContactItem>
          <ContactItem>
            <Phone size={18} />
            <a href="tel:+442034758421">+44 20 3475 8421</a>
          </ContactItem>
          <ContactItem>
            <Mail size={18} />
            <a href="mailto:hello@nysdill.com">hello@nysdill.com</a>
          </ContactItem>
          <ContactItem>
            <Clock size={18} />
            <span>Reception: 2:00 PM - 11:00 PM</span>
          </ContactItem>
        </FooterSection>

        {/* Hours & Info */}
        <FooterSection>
          <h3>Hotel Information</h3>
          <ContactItem>
            <Clock size={18} />
            <div>
              <strong>Check-in:</strong> 2:00 PM - 11:00 PM<br />
              <strong>Check-out:</strong> By 10:00 AM
            </div>
          </ContactItem>
          <p style={{ marginTop: '1rem' }}>
            Free WiFi • 24/7 Access<br />
            En-suite Bathrooms • Family Friendly<br />
            10 Minutes to All Terminals
          </p>
        </FooterSection>
      </FooterContent>

      <FooterBottom>
        <Copyright>
          © {currentYear} Nysdill Hotel Heathrow. All rights reserved.
        </Copyright>
        <LegalLinks>
          <LegalLink to="/privacy-policy">Privacy Policy</LegalLink>
          <LegalLink to="/terms-conditions">Terms & Conditions</LegalLink>
        </LegalLinks>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;